import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/Api";
import { toast } from "sonner";
import {
  MapPin,
  Calendar,
  Clock,
  User,
  Phone,
  Hash,
  CheckCircle2,
  XCircle,
  Navigation,
  FileText,
  Car,
} from "lucide-react";
import CarLoader from "../components/Loader1";

const MyTripDetails = () => {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [accepting, setAccepting] = useState(false);

  const navigate = useNavigate();
  const driverRegNo = localStorage.getItem("driverRegNo");

  useEffect(() => {
    fetchTrip();
  }, []);

  const fetchTrip = async () => {
    try {
      const res = await api.get(
        `/driver/assigned-trip?driver_regno=${driverRegNo}`,
      );
      const tripData = Array.isArray(res.data) ? res.data[0] : res.data;
      // console.log(tripData);
      setTrip(tripData || null);
    } catch (err) {
      toast.error("Trip fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (accept) => {
    setAccepting(true);
    try {
      await api.post("/driver/accept-trip", {
        enq_id: trip.enq_id,
        driver_regno: driverRegNo,
        action: accept ? "accept" : "reject",
      });
      toast.success(accept ? "Trip Accepted" : "Trip Rejected");
      setTrip({
        ...trip,
        accept_status: accept ? 1 : 2,
        otp_verified: trip.otp_verified || 0,
      });
    } catch (err) {
      toast.error("Action failed");
    } finally {
      setAccepting(false);
    }
  };

  const handleSendOtp = async () => {
    setSendingOtp(true);
    try {
      const res = await api.post("/driver/send-otp", { enq_id: trip.enq_id });

      // 2. Show DEV OTP Alert (Only if OTP is returned)
      // if (res.data.otp) {
      //   toast.success("OTP sent to guest successfully " + res.data.otp);
      // }

      navigate(`/start-trip/${trip.enq_id}`);
    } catch (err) {
      console.error(err);
      toast.error("OTP send failed");
    } finally {
      setSendingOtp(false);
    }
  };

  // --- LOADER ---
  if (loading) {
    return <CarLoader />;
  }

  if (!trip) {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center h-100 px-4 text-center"
        style={{ minHeight: "80vh" }}
      >
        <div className="bg-white p-4 rounded-circle shadow-sm mb-3">
          <Navigation size={48} className="text-secondary opacity-50" />
        </div>
        <h5 className="fw-bold text-dark">No Active Trips</h5>
        <p className="text-muted small">
          You don't have any assigned trips at the moment.
        </p>
      </div>
    );
  }

  // --- HELPERS ---
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="container-fluid bg-light min-dvh-100 p-0 pb-5">
      <div className="row justify-content-center g-0">
        <div className="col-12 col-md-8 col-lg-6 mx-auto">
          <div className="bg-primary text-white p-4 rounded-bottom-4 shadow-sm mb-4 position-relative overflow-hidden">
            <Car
              size={140}
              className="position-absolute text-white opacity-10"
              style={{ right: -30, top: -20, transform: "rotate(1deg)" }}
            />

            <div className="position-relative z-1 mt-2">
              <h2 className="fw-bold mb-1">Trip Details</h2>
              <p className="mb-0 opacity-75 small">Manage your assignment</p>
            </div>
          </div>

          <div className="px-2 pb-3">
            {/* 1. STATUS & REFERENCE CARD */}
            <div
              className="card border-0 mb-4 bg-white"
              style={{
                borderRadius: "1.25rem",
                boxShadow: "0 8px 24px rgba(149, 157, 165, 0.12)",
              }}
            >
              <div className="card-body p-4">
                {/* Header / Status Badge */}
                <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom border-light">
                  <span
                    className={`badge px-3 py-2 rounded-pill text-uppercase fw-bold`}
                    style={{
                      fontSize: "0.7rem",
                      letterSpacing: "0.5px",
                      backgroundColor:
                        trip.accept_status === 1
                          ? "#ecfdf5"
                          : trip.accept_status === 2
                            ? "#fef2f2"
                            : "#eff6ff",
                      color:
                        trip.accept_status === 1
                          ? "#059669"
                          : trip.accept_status === 2
                            ? "#dc2626"
                            : "#2563eb",
                    }}
                  >
                    {trip.accept_status === 1
                      ? "Confirmed"
                      : trip.accept_status === 2
                        ? "Rejected"
                        : "New Assignment"}
                  </span>
                  <span
                    className="text-primary fw-bold"
                    style={{ fontSize: "0.95rem", letterSpacing: "0.5px" }}
                  >
                    #{trip.confirmation_id}
                  </span>
                </div>

                {/* Details Row */}
                <div className="row g-3">
                  <div className="col-6">
                    <small
                      className="text-muted d-block mb-1 text-uppercase fw-bold"
                      style={{ fontSize: "0.65rem", letterSpacing: "1px" }}
                    >
                      Reference ID
                    </small>
                    <div className="d-flex align-items-center">
                      <div
                        className="bg-light rounded d-flex align-items-center justify-content-center me-2"
                        style={{ width: "24px", height: "24px" }}
                      >
                        <Hash size={12} className="text-secondary" />
                      </div>
                      <span
                        className="fw-bold text-dark"
                        style={{ fontSize: "0.95rem" }}
                      >
                        {trip.enqref_no}
                      </span>
                    </div>
                  </div>
                  <div className="col-6">
                    <small
                      className="text-muted d-block mb-1 text-uppercase fw-bold"
                      style={{ fontSize: "0.65rem", letterSpacing: "1px" }}
                    >
                      Guest Name
                    </small>
                    <div className="d-flex align-items-center">
                      <div
                        className="bg-light rounded d-flex align-items-center justify-content-center me-2"
                        style={{ width: "24px", height: "24px" }}
                      >
                        <User size={12} className="text-secondary" />
                      </div>
                      <span
                        className="fw-bold text-dark text-truncate"
                        style={{ fontSize: "0.95rem" }}
                      >
                        {trip.customer_name}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. ITINERARY TIMELINE CARD */}
            <div
              className="card border-0 mb-4 bg-white"
              style={{
                borderRadius: "1.25rem",
                boxShadow: "0 8px 24px rgba(149, 157, 165, 0.12)",
              }}
            >
              <div className="card-body p-4">
                <h6
                  className="fw-bold mb-4 text-secondary text-uppercase"
                  style={{ fontSize: "0.75rem", letterSpacing: "1px" }}
                >
                  Itinerary Schedule
                </h6>

                <div className="position-relative">
                  {/* Connecting Timeline Line */}
                  <div
                    className="position-absolute"
                    style={{
                      left: "23px",
                      top: "40px",
                      bottom: "40px",
                      width: "2px",
                      borderLeft: "2px dashed #e2e8f0",
                      zIndex: 0,
                    }}
                  ></div>

                  {/* PICKUP SECTION */}
                  <div className="d-flex position-relative z-1 mb-4">
                    <div
                      className="d-flex flex-column align-items-center me-3"
                      style={{ width: "48px" }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                        style={{
                          width: "48px",
                          height: "48px",
                          backgroundColor: "#e8f5e9",
                          color: "#10b981",
                        }}
                      >
                        <MapPin size={20} strokeWidth={2.5} />
                      </div>
                    </div>

                    <div className="flex-grow-1 pt-1 pb-2">
                      <span
                        className="badge text-uppercase mb-2 px-2 py-1"
                        style={{
                          fontSize: "0.6rem",
                          backgroundColor: "#ecfdf5",
                          color: "#059669",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Pickup
                      </span>
                      <h5
                        className="fw-bold text-dark mb-1"
                        style={{ fontSize: "1.05rem" }}
                      >
                        {trip.pickup_location}, {trip.pick_cityName}
                      </h5>

                      <div className="d-flex flex-wrap gap-2 mt-2">
                        <div
                          className="d-flex align-items-center text-secondary bg-light rounded-pill px-3 py-1"
                          style={{ fontSize: "0.8rem" }}
                        >
                          <Calendar size={13} className="me-2" />
                          <span className="fw-semibold">
                            {formatDate(trip.Pickup_time)}
                          </span>
                        </div>
                        <div
                          className="d-flex align-items-center text-secondary bg-light rounded-pill px-3 py-1"
                          style={{ fontSize: "0.8rem" }}
                        >
                          <Clock size={13} className="me-2" />
                          <span className="fw-semibold">
                            {trip.arrive_time_name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* DROP SECTION */}
                  <div className="d-flex position-relative z-1">
                    <div
                      className="d-flex flex-column align-items-center me-3"
                      style={{ width: "48px" }}
                    >
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center shadow-sm"
                        style={{
                          width: "48px",
                          height: "48px",
                          backgroundColor: "#fef2f2",
                          color: "#ef4444",
                        }}
                      >
                        <MapPin size={20} strokeWidth={2.5} />
                      </div>
                    </div>

                    <div className="flex-grow-1 pt-1">
                      <span
                        className="badge text-uppercase mb-2 px-2 py-1"
                        style={{
                          fontSize: "0.6rem",
                          backgroundColor: "#fef2f2",
                          color: "#dc2626",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Drop Off
                      </span>
                      <h5
                        className="fw-bold text-dark mb-1"
                        style={{ fontSize: "1.05rem" }}
                      >
                        {trip.dropoff_location}
                      </h5>
                      <p
                        className="text-secondary mb-2"
                        style={{ fontSize: "0.85rem" }}
                      >
                        {trip.drop_address || "Address not specified"}
                      </p>

                      <div className="d-flex flex-wrap gap-2">
                        <div
                          className="d-flex align-items-center text-secondary bg-light rounded-pill px-3 py-1"
                          style={{ fontSize: "0.8rem" }}
                        >
                          <Calendar size={13} className="me-2" />
                          <span className="fw-semibold">
                            {formatDate(trip.dropoff_time)}
                          </span>
                        </div>
                        <div
                          className="d-flex align-items-center text-secondary bg-light rounded-pill px-3 py-1"
                          style={{ fontSize: "0.8rem" }}
                        >
                          <Clock size={13} className="me-2" />
                          <span className="fw-semibold">
                            {trip.depart_time_name}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 3. GUEST INFO CARD */}
            <div
              className="card border-0 mb-4 bg-white"
              style={{
                borderRadius: "1.25rem",
                boxShadow: "0 8px 24px rgba(149, 157, 165, 0.12)",
              }}
            >
              <div className="card-body p-3 d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center me-3 bg-light text-secondary"
                    style={{ width: "50px", height: "50px" }}
                  >
                    <User size={22} strokeWidth={2} />
                  </div>
                  <div>
                    <h6
                      className="fw-bold mb-0 text-dark"
                      style={{ fontSize: "1rem" }}
                    >
                      {trip.customer_name}
                    </h6>
                    <small
                      className="text-secondary fw-medium"
                      style={{ fontSize: "0.75rem", letterSpacing: "0.3px" }}
                    >
                      Primary Guest
                    </small>
                  </div>
                </div>
                <a
                  href={`tel:${trip.cust_mobileno}`}
                  className="btn rounded-circle p-0 d-flex align-items-center justify-content-center text-white"
                  style={{
                    width: "48px",
                    height: "48px",
                    backgroundColor: "#10b981",
                    boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)",
                    transition: "transform 0.2s",
                  }}
                  onMouseOver={(e) =>
                    (e.currentTarget.style.transform = "scale(1.05)")
                  }
                  onMouseOut={(e) =>
                    (e.currentTarget.style.transform = "scale(1)")
                  }
                >
                  <Phone size={20} fill="currentColor" />
                </a>
              </div>
            </div>

            {/* 4. ACTIONS (Thick, thumb-friendly buttons) */}
            <div className="d-grid gap-3 mb-2">
              {trip.accept_status == null && (
                <>
                  <button
                    className="btn btn-primary rounded-pill shadow-sm fw-bold d-flex align-items-center justify-content-center py-3"
                    onClick={() => handleAccept(true)}
                    disabled={accepting}
                    style={{ fontSize: "0.95rem", letterSpacing: "0.5px" }}
                  >
                    {accepting ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <CheckCircle2 size={20} className="me-2" />
                    )}
                    ACCEPT TRIP
                  </button>
                  <button
                    className="btn btn-light text-white rounded-pill fw-bold d-flex align-items-center justify-content-center py-3"
                    onClick={() => handleAccept(false)}
                    disabled={accepting}
                    style={{
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                      backgroundColor: "#f01717",
                    }}
                  >
                    <XCircle size={20} className="me-2" />
                    CANCEL TRIP
                  </button>
                </>
              )}

              {/* {trip.accept_status === 1 && trip.otp_verified !== 1 && (
                <button
                  className="btn btn-dark rounded-pill py-3 fw-bold d-flex align-items-center justify-content-center text-white"
                  onClick={handleSendOtp}
                  disabled={sendingOtp}
                  style={{
                    background: "linear-gradient(135deg, #00a2ff, #101414)",
                    boxShadow: "0 8px 20px rgba(37, 99, 235, 0.25)",
                    fontSize: "0.95rem",
                    letterSpacing: "0.5px",
                  }}
                >
                  {sendingOtp ? (
                    <span className="spinner-border spinner-border-sm me-2"></span>
                  ) : (
                    <Navigation size={20} className="me-2" />
                  )}
                  SEND OTP TO CUSTOMER
                </button>
              )} */}

              {/* ###########  new  lated  add for  customer  share location bijay ################ */}

             {trip.accept_status === 1 && trip.otp_verified !== 1 && (
                <div className="d-grid gap-3">
                  <button
                    className="btn btn-outline-primary rounded-pill py-3 fw-bold d-flex align-items-center justify-content-center"
                    onClick={() => navigate(`/send-location`, { state: { hideEndTrip: true } })}
                    style={{ 
                      fontSize: "0.95rem", 
                      letterSpacing: "0.5px", 
                      border: "2px solid #0d6efd",
                      backgroundColor: "#f8fbff" 
                    }}
                  >
                    <MapPin size={20} className="me-2" />
                    TRACK CUSTOMER ON MAP
                  </button>

                  <button
                    className="btn btn-dark rounded-pill py-3 fw-bold d-flex align-items-center justify-content-center text-white"
                    onClick={handleSendOtp}
                    disabled={sendingOtp}
                    style={{
                      background: "linear-gradient(135deg, #00a2ff, #101414)",
                      boxShadow: "0 8px 20px rgba(37, 99, 235, 0.25)",
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    {sendingOtp ? (
                      <span className="spinner-border spinner-border-sm me-2"></span>
                    ) : (
                      <Navigation size={20} className="me-2" />
                    )}
                    ARRIVED: SEND OTP TO GUEST
                  </button>
                </div>
              )}

              {/* #######################  end  that ####################### */}

              {trip.accept_status === 1 &&
                trip.otp_verified === 1 &&
                trip.isTrip_end !== 1 && (
                  <button
                    className="btn rounded-pill py-3 fw-bold d-flex align-items-center justify-content-center text-white"
                    onClick={() => navigate(`/send-location`)}
                    style={{
                      backgroundColor: "#10b981",
                      boxShadow: "0 8px 20px rgba(16, 185, 129, 0.25)",
                      fontSize: "0.95rem",
                      letterSpacing: "0.5px",
                    }}
                  >
                    <MapPin size={20} className="me-2" />
                    VIEW LIVE MAP
                  </button>
                )}

              {trip.accept_status === 2 && (
                <div
                  className="alert border-0 d-flex align-items-center justify-content-center py-3 mb-0"
                  style={{
                    borderRadius: "1rem",
                    backgroundColor: "#fef2f2",
                    color: "#dc2626",
                  }}
                >
                  <XCircle size={20} className="me-2" />
                  <span className="fw-bold" style={{ fontSize: "0.95rem" }}>
                    You have rejected this trip
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyTripDetails;